import React, { Fragment } from "react";
import { useIntl } from "react-intl";
import { compose } from "recompose";
import {
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { bool, func, object, string } from "prop-types";

import { FormSelect, FormTextField, Loading, Switch } from '../../../components';

import { getI18n, getI18nFormattedMessage } from "../../../utils/intl";
import { get } from "lodash";

const HASH_ALGORITHM_OPTIONS = [
  {
    uuid: 'SHA-512',
    text: getI18nFormattedMessage('label.settings.application.keysettings.shared.secret.security.hash.sha512'),
  }, {
    uuid: 'SHA-384',
    text: getI18nFormattedMessage('label.settings.application.keysettings.shared.secret.security.hash.sha384'),
  }, {
    uuid: 'SHA-256',
    text: getI18nFormattedMessage('label.settings.application.keysettings.shared.secret.security.hash.sha256'),
  }, {
    uuid: 'SHA-1',
    text: getI18nFormattedMessage('label.settings.application.keysettings.shared.secret.security.hash.sha1'),
    disabled: true,
  }, {
    uuid: 'MD5',
    text: getI18nFormattedMessage('label.settings.application.keysettings.shared.secret.security.hash.md5'),
    disabled: true,
  },
];

const preventSignsAndFraction = event => {
  if (event.key === '-' || event.key === '+' || event.key === '.') {
    event.preventDefault();
  }
};

export const ApplicationSecretSettings = (props) => {
  const {
    classes,
    executeExpiryJob,
    isExecutingExpiryJob,
    isExecutingReportingJob,
    isLoading,
    formValues,
    expiryReportingError,
    isFormDirty,
    handleInputChange,
    enablingExpiry,
    apiKeySettings,
  } = props;
  const intl = getI18n(useIntl());

  const {
    allowPlainSecrets,
    allowHashedSecrets,
    algorithm,
    expireKeys,
    daysUntilExpiry,
    applyToKeysWithoutExpiry,
    applyToKeysWithLongerExpiry,
  } = formValues;

  const getSecretsLabel = ({ label, labelHelpText }) => (
    <div className={classes.secretLabelContainer}>
      <div className={classes.secretLabel}>
        {label}
      </div>
      <div className={classes.secretLabelHelpText}>
        {labelHelpText}
      </div>
    </div>
  );
  const handleUntilDaysChange = (value) => {
    if(value < 2147483647) { //Int max range
      handleInputChange({ target: { name: 'daysUntilExpiry', value } });
    }
  }
  const renderSharedSecretSecurity = () =>
    (<Fragment>
      <Typography variant="h4">
        {intl.getI18nMessage("label.settings.application.keysettings.shared.secret.security.subtitle")}
      </Typography>
      <Typography variant="body1">
        {intl.getI18nMessage("label.settings.application.keysettings.title.helpertext.1")}
      </Typography>
      <div className={classes.sharedSecretControls}>
        <Typography variant="body1">
          {intl.getI18nMessage("label.settings.application.keysettings.title.helpertext.2")}
        </Typography>
        <div className={classes.secretContainer}>
          <FormControlLabel
            control = {
              <Checkbox
                id="key-settings-allow-plain-secrets"
                name={'allowPlainSecrets'}
                onChange={handleInputChange}
                checked={allowPlainSecrets}
              />
            }
            label={
                getSecretsLabel({
                  label: intl.getI18nMessage("label.settings.application.keysettings.shared.secret.security.allow.plaintext.secrets"),
                  labelHelpText: intl.getI18nMessage("label.settings.application.keysettings.shared.secret.security.allow.plaintext.secrets.helptext"),
              })
            }
          />
        </div>
        <div className={classes.secretContainer}>
          <FormControlLabel
            control = {
              <Checkbox
                id="key-settings-allow-hashed-secrets"
                name={'allowHashedSecrets'}
                onChange={handleInputChange}
                checked={allowHashedSecrets}
              />
            }
            label={
              getSecretsLabel({
                label:  intl.getI18nMessage("label.settings.application.keysettings.shared.secret.security.allow.hashed.secrets"),
                labelHelpText: intl.getI18nMessage("label.settings.application.keysettings.shared.secret.security.allow.hashed.secrets.helptext"),
              })
            }
          />
          <Collapse in={allowHashedSecrets}>
            <FormSelect
              id="key-settings-hash-algorithm-select"
              data={HASH_ALGORITHM_OPTIONS}
              handleChange={(value) => handleInputChange({ target: { name: 'algorithm', value: value } })}
              name={intl.getI18nMessage("label.settings.application.keysettings.shared.secret.security.hash.algorithm")}
              noNoneOption
              noNativeSelect
              value={algorithm}
            />
          </Collapse>
        </div>
      </div>
    </Fragment>);

  const disableJobExecutionButton= isFormDirty;

  const renderKeyExpiry = () =>
    (<Fragment>
        <div className={classes.keysSubtitleContainer}>
          <div>
            <Typography variant="h4">
              {intl.getI18nMessage("label.settings.application.keysettings.expire.keys.subtitle")}
            </Typography>
          </div>
          <div>
            <Switch
              checked={expireKeys}
              id={'key-settings-expire-keys'}
              onChange={(checked) => handleInputChange({ target: { name: 'expireKeys', checked } })}
            />
          </div>
        </div>
        <Divider />
        <Typography className={classes.switchHelpText} variant="body1">
          {intl.getI18nMessage("label.settings.application.keysettings.expire.keys.subtitle.helptext")}
        </Typography>
        <Collapse
          in={expireKeys}
          unmountOnExit
        >
          <FormTextField
            name={intl.getI18nMessage('label.settings.application.keysettings.expire.keys.days.untill.expiry')}
            id={'key-settings-days-until-expiry'}
            data-apim-test={'key-settings-days-until-expiry'}
            variant="standard"
            value={`${daysUntilExpiry}`}
            handleChange={(value) => handleUntilDaysChange(value)}
            onKeyPress={preventSignsAndFraction}
            error={expiryReportingError}
            helperText={intl.getI18nMessage('label.settings.application.keysettings.expire.keys.days.untill.expiry.helptext')}
            type={'number'}
          />
          {(enablingExpiry || parseInt(get(apiKeySettings, 'applicationApiKeyExpiry.expiryDays')) !== parseInt(daysUntilExpiry)) &&
          (<div className={classes.expireOptions}><div>
              <FormControlLabel
                control = {
                  <Checkbox
                    data-apim-test={'key-settings-applyToKeysWithoutExpiry'}
                    name={'applyToKeysWithoutExpiry'}
                    onChange={handleInputChange}
                    checked={applyToKeysWithoutExpiry}
                  />
                }
                label={intl.getI18nMessage('label.settings.application.keysettings.expire.keys.apply.without.expiry.date')}
              />
            </div>
            <div>
              <FormControlLabel
                control = {
                  <Checkbox
                    data-apim-test={'key-settings-applyToKeysWithLongerExpiry'}
                    name={'applyToKeysWithLongerExpiry'}
                    onChange={handleInputChange}
                    checked={applyToKeysWithLongerExpiry}
                    disabled={enablingExpiry}
                  />
                }
                label={intl.getI18nMessage('label.settings.application.keysettings.expire.keys.apply.longer.expiry.date')}
              />
            </div></div>)}
          {renderExpiryJobButton()}
          <div className={classes.emptyBlock}/>
        </Collapse>
    </Fragment>);

  const renderExpiryJobButton = () => {
    return (<div className={classes.expiryJobButtonContainer}>
      <div>
        <Tooltip
          arrow
          placement="top"
          title={disableJobExecutionButton ? getI18nFormattedMessage('label.settings.application.keysettings.expire.job.button.tooltip') : ''}
        >
          <span>
            <Button
              className={classes.expireJobBtn}
              data-apim-test="execute-expiry-job-button"
              disabled={disableJobExecutionButton}
              id={'execute-expiry-job-button'}
              onClick={executeExpiryJob}
              variant="outlined"
              color="primary"
            >
              {intl.getI18nMessage("label.settings.application.keysettings.expire.job.button.text")}
            </Button>
          </span>
        </Tooltip>
      </div>
      <div
        className={classes.expireJobBtnHelpTextContainer}
      >
        <span
          className={classes.expireJobBtnHelpText}
        >
          {intl.getI18nMessage("label.settings.application.keysettings.expire.job.button.helptext")}
        </span>
      </div>
    </div>);
  };

  return(
    <div className={classes.details}>
      {(isLoading || isExecutingExpiryJob || isExecutingReportingJob)
        && <Loading pageLoader={true} />}
      {renderSharedSecretSecurity()}
      {renderKeyExpiry()}
    </div>
  );
};

ApplicationSecretSettings.propTypes = {
  classes: object,
  formValues: object,
  expiryJobStatus: string,
  isExecutingExpiryJob: bool,
  executeExpiryJob: func,
  isExecutingReportingJob: bool,
  isLoading: bool,
  expiryReportingError: bool,
  isFormDirty: bool,
  handleInputChange: func,
  enablingExpiry: bool,
  apiKeySettings: object,
};

export default compose()(ApplicationSecretSettings);
